import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowDown, ArrowUpRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import FieldnotesForm from "@/components/FieldnotesForm";
import PatternCard from "@/components/PatternCard";
import { Reveal, EASE } from "@/components/Reveal";
import { useTitle } from "@/hooks/use-title";
import { IDEAS, CATEGORIES, YOUTUBE_URL, HIDDEN_SLUGS } from "@/data/content";

const FEATURED = IDEAS.find((i) => i.featured);

const PATTERN_STEPS = [
  { label: "SYMPTOM", text: FEATURED.pattern.symptom },
  { label: "FINDING", text: FEATURED.pattern.finding },
  { label: "ROOT CAUSE", text: FEATURED.pattern.rootCause },
  { label: "RECOMMENDATION", text: FEATURED.pattern.recommendation, hot: true },
];

function CategoryNav({ active, onChange }) {
  return (
    <div className="sticky top-16 z-30 border-b border-line/60 bg-ink/90 backdrop-blur-md lg:top-[72px]" data-testid="category-nav">
      <div className="wrap flex flex-wrap items-center gap-2 py-4" role="group" aria-label="Filter by problem area">
        {CATEGORIES.map((c) => {
          const isActive = active === c.id;
          return (
            <button
              key={c.id}
              onClick={() => onChange(c.id)}
              aria-pressed={isActive}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                isActive
                  ? "border-glow/60 bg-glow/5 text-glow"
                  : "border-line/60 text-slate-500 hover:border-slate-500 hover:text-slate-200"
              }`}
              data-testid={`category-filter-${c.id}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-line/50" data-testid="hub-hero">
      <div className="bg-grid absolute inset-0 opacity-30" />
      <div className="wrap relative py-24 lg:py-32">
        <Reveal>
          <p className="kicker">Operating Reality</p>
          <h1 className="mt-6 max-w-4xl text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            How Work Actually Works
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
            The processes, decisions, dependencies and systems behind everyday work.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 flex max-w-3xl flex-col gap-px border border-line/50 bg-line/50 sm:flex-row" data-testid="hub-questions">
            {["What is actually happening?", "Why is it happening?", "What should change?"].map((q, i) => (
              <div key={q} className="flex flex-1 items-center gap-3 bg-ink px-5 py-4">
                <span className="font-mono text-[10px] tracking-[0.25em] text-glow/70">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300">{q}</span>
              </div>
            ))}
          </div>
          <p className="mt-5 max-w-xl text-xs leading-relaxed text-slate-600">
            We don't start with “what technology should you buy?”. We start here.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function FeaturedReality() {
  return (
    <section className="border-b border-line/50" data-testid="featured-reality-section">
      <div className="wrap grid gap-14 py-20 lg:grid-cols-12 lg:py-28">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="kicker">Featured Reality</p>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              A closer look at the problems hiding underneath everyday work.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-12 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              <span className="inline-block h-1.5 w-1.5 rotate-45 border border-glow/60" />
              {FEATURED.tag}
            </p>
            <h2 className="mt-5 text-balance text-3xl font-medium leading-[1.15] tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              {FEATURED.title}
            </h2>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-slate-300">
              {FEATURED.insight}
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
              {FEATURED.href ? (
                <Link to={FEATURED.href} className="btn-primary" data-testid="featured-watch-cta">
                  Read the case study <ArrowRight size={14} />
                </Link>
              ) : (
                <a
                  href={YOUTUBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  data-testid="featured-watch-cta"
                >
                  Watch <ArrowRight size={14} />
                </a>
              )}
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500 transition-colors duration-300 hover:text-glow"
                data-testid="featured-youtube-link"
              >
                Watch the short video
                <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <div className="border border-line/60 bg-charcoal/30 p-7 lg:p-10" data-testid="featured-pattern">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                The Operating Reality pattern
              </p>
              <div className="relative mt-8">
                <div className="absolute bottom-5 left-[5px] top-2 w-px bg-line/70" />
                <motion.div
                  className="absolute bottom-5 left-[5px] top-2 w-px origin-top bg-glow/70"
                  initial={{ scaleY: 0 }}
                  whileInView={{ scaleY: 1 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 2, ease: EASE }}
                  data-testid="featured-pattern-line"
                />
                <ol className="space-y-7">
                  {PATTERN_STEPS.map((s, i) => (
                    <li key={s.label} className="relative pl-8" data-testid={`pattern-step-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <motion.span
                        className={`absolute left-0 top-1.5 h-[11px] w-[11px] rounded-full border bg-ink ${
                          s.hot ? "border-glow" : "border-slate-600"
                        }`}
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, delay: 0.3 + i * 0.3, ease: EASE }}
                      >
                        {s.hot && <span className="absolute inset-[3px] rounded-full bg-glow" />}
                      </motion.span>
                      <Reveal delay={0.25 + i * 0.25}>
                        <p className={`font-mono text-[10px] uppercase tracking-[0.3em] ${s.hot ? "text-glow" : "text-slate-600"}`}>
                          {s.label}
                        </p>
                        <p className={`mt-2 text-sm leading-relaxed ${s.hot ? "text-slate-100" : "text-slate-300"}`}>
                          {s.text}
                        </p>
                        {i < PATTERN_STEPS.length - 1 && (
                          <ArrowDown size={12} className="mt-3 text-slate-700" aria-hidden="true" />
                        )}
                      </Reveal>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Patterns({ active }) {
  const visible = IDEAS.filter((i) => !HIDDEN_SLUGS.includes(i.slug));
  const filtered = active === "all" ? visible : visible.filter((i) => i.cats.includes(active));
  return (
    <section className="border-b border-line/50" data-testid="patterns-section">
      <div className="wrap py-20 lg:py-28">
        <SectionHeader
          kicker="Patterns"
          title="The problems that keep appearing, regardless of the organisation or technology."
        />
        <div className="mt-14 grid gap-px border border-line/50 bg-line/50 md:grid-cols-2" data-testid="patterns-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((idea) => (
              <motion.div
                key={idea.slug}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="bg-ink"
              >
                <PatternCard idea={idea} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        <p className="mt-8 max-w-xl text-xs leading-relaxed text-slate-600" data-testid="patterns-count-note">
          {filtered.length === visible.length
            ? "Every pattern, across every area. The index grows as new realities are documented."
            : `Showing ${filtered.length} ${filtered.length === 1 ? "pattern" : "patterns"} in this area.`}
        </p>
      </div>
    </section>
  );
}

function Frameworks() {
  return (
    <section className="border-b border-line/50 bg-charcoal/20" data-testid="frameworks-section">
      <div className="wrap py-20 lg:py-28">
        <SectionHeader
          kicker="Frameworks"
          title="Practical ways to understand what is really happening before you change it."
        />
        <div className="mt-14 grid gap-px border border-line/50 bg-line/50 lg:grid-cols-3">
          <Reveal className="bg-ink lg:col-span-1">
            <div className="flex h-full flex-col justify-between gap-10 border-glow/30 p-8 lg:p-10" data-testid="framework-diagnostic-card">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-glow/80">
                  Diagnostic — 15 questions
                </p>
                <h3 className="mt-4 text-xl font-medium tracking-tight text-slate-100">
                  Operating Reality Diagnostic
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  15 questions to help identify what's really broken before you automate it.
                </p>
              </div>
              <Link
                to="/diagnostic"
                className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-glow"
                data-testid="frameworks-diagnostic-cta"
              >
                Explore the Diagnostic
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          {[
            { title: "Checklists & Assessment Tools", text: "Structured instruments for mapping how work actually flows." },
            { title: "Guides & Toolkits", text: "The Operating Reality frameworks, packaged for practical use." },
          ].map((f, i) => (
            <Reveal key={f.title} delay={0.08 * (i + 1)} className="bg-ink">
              <div className="flex h-full flex-col justify-between gap-10 p-8 lg:p-10" data-testid={`framework-placeholder-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div>
                  <h3 className="text-xl font-medium tracking-tight text-slate-200">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">{f.text}</p>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-600">
                  In development
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FieldnotesCta() {
  return (
    <section className="border-b border-line/50" data-testid="hub-fieldnotes-section">
      <div className="wrap grid gap-12 py-20 lg:grid-cols-12 lg:py-28">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="kicker">Fieldnotes</p>
            <h2 className="mt-5 text-balance text-3xl font-medium leading-[1.2] tracking-tight text-slate-100 lg:text-4xl">
              Operating Reality Fieldnotes
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">
              One practical observation about how organisations actually work.
            </p>
            <p className="mt-3 max-w-lg text-base leading-relaxed text-slate-400">
              No hype. No generic productivity advice. Just useful ideas about processes,
              technology and transformation.
            </p>
          </Reveal>
        </div>
        <div className="flex items-center lg:col-span-5">
          <Reveal delay={0.1} className="w-full">
            <FieldnotesForm compact />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ConsultingBridge() {
  return (
    <section data-testid="consulting-bridge-section">
      <div className="wrap py-20 lg:py-32">
        <Reveal className="max-w-3xl">
          <p className="kicker">The next question</p>
          <h2 className="mt-6 text-balance text-4xl font-semibold leading-[1.1] tracking-tight text-slate-50 sm:text-5xl">
            See something familiar?
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
            If one of these problems looks familiar, the next question isn't always “what
            software should we buy?”
          </p>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-200 sm:text-lg">
            It's: what's actually happening?
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link to="/consulting" className="btn-primary" data-testid="bridge-consulting-cta">
              Explore Consulting <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="btn-ghost" data-testid="bridge-conversation-cta">
              Start a conversation
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Ideas() {
  useTitle("How Work Actually Works — Operating Reality");
  const [active, setActive] = useState("all");
  return (
    <main className="pt-16 lg:pt-[72px]" data-testid="how-work-actually-works-page">
      <Hero />
      <CategoryNav active={active} onChange={setActive} />
      <FeaturedReality />
      <Patterns active={active} />
      <Frameworks />
      <FieldnotesCta />
      <ConsultingBridge />
    </main>
  );
}
